import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Stack,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Popover
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  FilterList,
  CalendarToday,
  Clear,
  Person,
  Home,
  Business,
  Villa,
  ViewColumn
} from '@mui/icons-material';
import { investorApi, walletApi } from '../../api/api';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs from 'dayjs';

// Status configuration with colors
const statusConfig = {
  Pending: { color: 'warning', label: 'Pending' },
  InProgress: { color: 'info', label: 'In Progress' },
  Approved: { color: 'success', label: 'Approved' },
  Rejected: { color: 'error', label: 'Rejected' }
};

// Property type configuration
const propertyTypeConfig = {
  Apartment: { color: 'primary', icon: <Home /> },
  Project: { color: 'success', icon: <Business /> },
  Property: { color: 'warning', icon: <Home /> },
  'Individual Villa': { color: 'secondary', icon: <Villa /> }
};

const BookedProperties = () => {
  const [siteVisits, setSiteVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("all");
  const [pageSize, setPageSize] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);

  const handleColumnIconClick = (event) => {
    setColumnAnchorEl(event.currentTarget);
  };

  const handleColumnClose = () => {
    setColumnAnchorEl(null);
  };

  const openColumn = Boolean(columnAnchorEl);
  const columnId = openColumn ? "column-popover" : undefined;

  const columns = (handleMenuClick) => [
    {
      field: 'Id',
      headerName: 'ID',
      width: 70,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'Username',
      headerName: 'USER',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="action" />
          <Typography variant="body2" noWrap>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'displayName',
      headerName: 'PROPERTY DETAILS',
      width: 300,
      renderCell: (params) => {
        const typeConfig = propertyTypeConfig[params.row.PropertyType] || {};

        if (params.row.PropertyType === "Apartment") {
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {params.row.ApartmentName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {params.row.PropertyName}
                </Typography>
              </Box>
            </Box>
          );
        } else if (params.row.PropertyType === "Project") {
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {params.row.ProjectName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {params.row.PropertyName || 'Project'}
                </Typography>
              </Box>
            </Box>
          );
        } else {
          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {params.row.PropertyName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {params.row.PropertyType || 'Property'}
                </Typography>
              </Box>
            </Box>
          );
        }
      }
    },
    {
      field: 'PropertyType',
      headerName: 'TYPE',
      width: 130,
      renderCell: (params) => {
        const config = propertyTypeConfig[params.value] || {};
        return (
          <Chip
            label={params.value}
            color={config.color}
            variant="outlined"
            size="small"
          />
        );
      }
    },
    {
      field: 'advance_payment',
      headerName: 'ADVANCE',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" sx={{ color: "#363636ff" }}>
          ₹{params.value?.toLocaleString() || '0'}
        </Typography>
      )
    },
    {
      field: 'property_value',
      headerName: 'PROPERTY VALUE',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          ₹{params.value?.toLocaleString() || '0'}
        </Typography>
      )
    },
    {
      field: 'MobileNumber',
      headerName: 'MOBILE',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" sx={{ color: "#0871c5" }}>
          {params.value?.toLocaleString() || '0'}
        </Typography>
      )
    },
    {
      field: 'Email',
      headerName: 'EMAIL',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" sx={{ color: "#0871c5" }}>
          {params.value?.toLocaleString() || 'NA'}
        </Typography>
      )
    },
    {
      field: 'payment_status',
      headerName: 'STATUS',
      width: 130,
      renderCell: (params) => {
        const config = statusConfig[params.value] || { color: 'default', label: params.value };
        return (
          <Chip
            label={config.label}
            color={config.color}
            variant="filled"
            size="small"
          />
        );
      },
    },
    {
      field: 'payment_date',
      headerName: 'PAYMENT DATE',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? dayjs(params.value).format('DD MMM YYYY') : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Actions">
          <IconButton
            size="small"
            onClick={(e) => handleMenuClick(e, params.row)}
            color="primary"
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      ),
    }
  ];

  const [selectedFields, setSelectedFields] = useState(
    columns(() => { }).map((col) => col.field)
  );
  const displayedColumns = columns(() => { }).filter((col) =>
    selectedFields.includes(col.field)
  );

  useEffect(() => {
    fetchAndFilter();
  }, [filterDate, searchKeyword, paymentStatus, propertyTypeFilter]);

  const fetchAndFilter = async () => {
    setLoading(true);
    try {
      const res = await investorApi.getAllBookedProperties();
      let data = res.data || [];

      // Apply filters
      if (filterDate) {
        data = data.filter((visit) => {
          const visitDate = dayjs(visit.payment_date).format('YYYY-MM-DD');
          return visitDate === filterDate;
        });
      }

      if (searchKeyword.trim() !== '') {
        const keyword = searchKeyword.toLowerCase();
        data = data.filter(
          (visit) =>
            visit.Username?.toLowerCase().includes(keyword) ||
            visit.PropertyName?.toLowerCase().includes(keyword) ||
            visit.ApartmentName?.toLowerCase().includes(keyword) ||
            visit.ProjectName?.toLowerCase().includes(keyword) ||
            visit.MobileNumber?.toString().includes(keyword) ||
            visit.Email?.toLowerCase().includes(keyword)
        );
      }

      if (paymentStatus !== "all") {
        data = data.filter(
          (visit) => visit.payment_status === paymentStatus
        );
      }

      if (propertyTypeFilter !== "all") {
        data = data.filter(
          (visit) => visit.PropertyType === propertyTypeFilter
        );
      }

      // Custom sort order for statuses
      const statusOrder = {
        Pending: 1,
        InProgress: 2,
        Approved: 3,
        Rejected: 4
      };
      data = [...data].sort(
        (a, b) => (statusOrder[a.payment_status] || 99) - (statusOrder[b.payment_status] || 99)
      );

      setSiteVisits(res.data || []);
      setFilteredVisits(data);
    } catch (error) {
      console.error("Failed to fetch booked properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateClick = async () => {
    if (!selectedRow?.Id) return;

    setOpenModal(true);
    setLoading(true);
    try {
      setPaymentDetails(null);
      const response = await walletApi.getPaymentDetailsById(selectedRow.Id);
      setPaymentDetails(response.data?.data || null);
    } catch (error) {
      console.error("Failed to fetch payment details", error);
      setPaymentDetails(null);
    } finally {
      setLoading(false);
    }

    setNewStatus(selectedRow?.payment_status || '');
    handleMenuClose();
  };

  const handleUpdateStatus = async () => {
    if (!selectedRow) return;

    setUpdating(true);
    const propertyTypeMap = {
      "Property": 1,
      "Project": 2,
      "Apartment": 3,
      "Individual Villa": 4
    };

    const payload = {
      payment_id: parseInt(selectedRow.Id),
      status: newStatus,
      propertyId: selectedRow.propertyId ? parseInt(selectedRow.propertyId) : null,
      apartmentId: selectedRow.apartmentId ? parseInt(selectedRow.apartmentId) : null,
      propertyType: propertyTypeMap[selectedRow.PropertyType] || 0,
      userId: selectedRow.userId ? parseInt(selectedRow.userId) : 0
    };

    try {
      const response = await investorApi.approveAdvancePayment(payload);

      if (response.data.success) {
        alert(response.data.message);
        setOpenModal(false);
        // Refresh table
        await fetchAndFilter();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      alert("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const clearFilters = () => {
    setFilterDate('');
    setSearchKeyword('');
    setPaymentStatus('all');
    setPropertyTypeFilter('all');
  };

  const getStatusCount = (status) => {
    return siteVisits.filter(visit => visit.payment_status === status).length;
  };

  const totalProperties = siteVisits.length;
  const filteredCount = filteredVisits.length;

  const hasActiveFilters = filterDate || paymentStatus !== 'all' || searchKeyword || propertyTypeFilter !== 'all';

  return (
    <Box sx={{ marginTop: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
          Booked Property
        </Typography>

      </Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {totalProperties}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Bookings
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {getStatusCount('Pending')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {getStatusCount('InProgress')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {getStatusCount('Approved')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
            <Typography variant="h4" fontWeight="bold" color="error.main">
              {getStatusCount('Rejected')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rejected
            </Typography>
          </Card>
        </Grid>
      </Grid>
      {/* Header */}
      <Card
        sx={{
          flexGrow: 1,
          border: "1px solid lightgray",
          borderRadius: "5px",
          marginBottom: 2
        }}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
            direction="row"
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {/* Search Field */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search By Key words"
                variant="outlined"
                fullWidth
                size="small"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                }}
              />
            </Grid>

            {/* Date Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Payment Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="InProgress">In Progress</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Property Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={propertyTypeFilter}
                  onChange={(e) => setPropertyTypeFilter(e.target.value)}
                  label="Property Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="Property">Property</MenuItem>
                  <MenuItem value="Project">Project</MenuItem>
                  <MenuItem value="Apartment">Apartment</MenuItem>
                  <MenuItem value="Individual Villa">Individual Villa</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} sm={6} md={2}>
              <IconButton
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  color: hasActiveFilters ? '#1976d2' : '#ccc',
                  border: '1px solid',
                  borderColor: hasActiveFilters ? '#1976d2' : '#ccc',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: hasActiveFilters ? '#e3f2fd' : 'transparent'
                  }
                }}
              >
                <Clear />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  Clear Filters
                </Typography>
              </IconButton>
            </Grid>

            {/* Icon Button (Right end) */}
            <Grid item xs={12} sm="auto" md="auto">
              <IconButton onClick={handleColumnIconClick}>
                <ViewColumn />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Data Grid */}
      <Card style={{ height: "100vh", overflowX: "hidden", marginTop: "10px", backgroundColor: "#f4f1f1ff" }}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredVisits}
              columns={displayedColumns.map(col => {
                if (col.field === 'actions') {
                  return {
                    ...col,
                    renderCell: (params) => (
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, params.row)}
                          color="primary"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  };
                }
                return col;
              })}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(newSize) => setPageSize(newSize)}
              disableSelectionOnClick
              getRowId={(row) => row.Id}
              sx={{
                height: 650,
                border: '1px solid #ccc',
                borderRadius: 2,
                fontSize: '0.9rem',
                backgroundColor: '#ffffff',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f0f0f0ff',
                },
                '& .MuiDataGrid-cell': {
                  color: '#333',
                  borderBottom: '1px solid #ddd',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f0f0f0ff',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTablePagination-root': {
                  fontSize: '0.875rem',
                },
              }}
            />
          )}
        </Box>
      </Card>

      {/* Column Selection Popover */}
      <Popover
        id={columnId}
        open={openColumn}
        anchorEl={columnAnchorEl}
        onClose={handleColumnClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Columns
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {columns(() => { }).map((col) => (
              <FormControl
                key={col.field}
                component="fieldset"
                sx={{ m: 0, p: 0 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFields.includes(col.field)}
                      onChange={() => {
                        setSelectedFields((prev) =>
                          prev.includes(col.field)
                            ? prev.filter((f) => f !== col.field)
                            : [...prev, col.field]
                        );
                      }}
                      sx={{
                        '&.Mui-checked': {
                          color: 'skyblue',
                        },
                      }}
                    />
                  }
                  label={col.headerName}
                  sx={{ m: 0 }}
                />
              </FormControl>
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleUpdateClick}>Update Payment Status</MenuItem>
      </Menu>

      {/* Update Status Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Update Payment Status
        </DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <>
              <Typography variant="h6" gutterBottom>
                {selectedRow.PropertyName || selectedRow.ApartmentName || selectedRow.ProjectName}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Advance Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    ₹{(paymentDetails?.advance_payment || selectedRow.advance_payment)?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Property Value
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ₹{(paymentDetails?.property_value || selectedRow.property_value)?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Status
                  </Typography>
                  <Chip
                    label={paymentDetails?.payment_status || selectedRow.payment_status}
                    color={statusConfig[paymentDetails?.payment_status || selectedRow.payment_status]?.color || 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Booked By
                  </Typography>
                  <Typography variant="body1">
                    {selectedRow.Username}
                  </Typography>
                </Grid>
              </Grid>

              {paymentDetails?.screenshot_url ? (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Payment Screenshot:
                  </Typography>
                  <img
                    src={paymentDetails.screenshot_url}
                    alt="Payment Screenshot"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      borderRadius: 8,
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </Box>
              ) : (
                loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )
              )}

              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    label="Update Status"
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="InProgress">In Progress</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            color="primary"
            disabled={updating || !newStatus}
          >
            {updating ? <CircularProgress size={20} color="inherit" /> : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookedProperties;